import { useCallback, useEffect, useMemo, useRef } from "react";
import { Avatar, Box, Button, Stack, Typography } from "@mui/material";
import Check from "@mui/icons-material/Check";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Paths } from "src/constants/paths";
import { useUpdateVisitMutation } from "src/api/services/visitApi";
import { useVisit } from "src/context/VisitProvider";
import WhiteCard from "src/shared/components/WhiteCard";
import { useTranslation } from "react-i18next";
import providerIconFemale from "src/assets/providerIconFemale.png";
import { useProduct } from "src/context/ProductProvider";

const PaymentConfirmation = () => {
  const isCompleted = useVisit((v) => v.completed_at != null);
  const [updateVisit] = useUpdateVisitMutation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const visitId = useVisit((v) => v.slug);
  const metadata = useVisit(
    (v) => v.metadata as Record<string | number, unknown>,
  );
  const category = useProduct((p) => p.category.name.toLowerCase());
  const billingModel = useProduct((p) => p.billing_model);
  const page = "payment-confirmation";
  const { t } = useTranslation();
  const [serachParams] = useSearchParams();
  const form = serachParams.get("form")?.toLowerCase();
  const variant = serachParams.get("variant")?.toLowerCase();

  const visitCalledRef = useRef(false);

  const steps = useMemo(() => {
    return [
      t(`${page}.${category}.card2.steps.0`),
      t(`${page}.${category}.card2.steps.1`),
      t(`${page}.${category}.card2.steps.2`),
    ];
  }, [category, t]);

  const handleContinue = useCallback(() => {
    if (form) {
      serachParams.delete("form");
    }
    if (variant) {
      serachParams.delete("variant");
    }

    return navigate(Paths.PATIENT_DASHBOARD);
  }, [form, navigate, serachParams, variant]);

  useEffect(() => {
    let isMounted = true;

    const doSideEffects = async () => {
      if (
        isMounted &&
        !visitCalledRef.current &&
        visitId &&
        isCompleted == false
      ) {
        visitCalledRef.current = true;

        await updateVisit({
          slug: visitId,
          data: {
            status: "completed",
            completed_at: new Date().toISOString(),
            metadata: {
              ...metadata,
              medication_form: form,
            },
          },
        });
      }
    };

    doSideEffects();

    return () => {
      isMounted = false;
    };
  }, [form, isCompleted, metadata, updateVisit, visitId]);

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);

    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  return (
    <Stack gap={4} width="100%">
      <WhiteCard gap={2} sx={{ padding: "16px" }}>
        {isMobile ? (
          <Box display="flex" justifyContent="space-between">
            <Stack gap={1}>
              <Typography variant="body2">
                {t(`${page}.card1.caption`)}
              </Typography>
              <Typography variant="body1" fontWeight={700}>
                {t(`${page}.${category}.card1.text`)}
              </Typography>
            </Stack>
            <Avatar src={providerIconFemale} alt="Provider" />
          </Box>
        ) : (
          <>
            <Typography variant="body2">
              {t(`${page}.card1.caption`)}
            </Typography>
            <Box display="flex" gap={2} alignItems="center">
              <Avatar
                src={providerIconFemale}
                alt="Provider"
                sx={{ width: "60px", height: "60px" }}
              />
              <Typography variant="body1" fontWeight={700}>
                {t(`${page}.${category}.card1.text`)}
              </Typography>
            </Box>
          </>
        )}
      </WhiteCard>
      <WhiteCard sx={{ padding: "16px" }} gap={2}>
        <Typography variant="body2">{t(`${page}.card2.caption`)}</Typography>
        <Typography variant="body1" fontWeight={700}>
          {t(`${page}.${category}.card2.text`)}
        </Typography>
        {steps.map((s, idx) => (
          <Box key={idx} display="flex" gap={1}>
            <Check sx={{ color: "#FF8175" }} />
            <Typography variant="subtitle1" sx={{ lineHeight: "21px" }}>
              {s}
            </Typography>
          </Box>
        ))}
      </WhiteCard>
      <WhiteCard sx={{ padding: "16px" }} gap={1}>
        <Typography variant="body2" color="#3BB273">
          {t(`${page}.card3.caption`)}
        </Typography>
        <Typography variant="body1" fontWeight={700}>
          {t(`${page}.${billingModel}.card3.text`)}
        </Typography>
      </WhiteCard>
      <Button onClick={handleContinue}>{t(`${page}.submit`)}</Button>
    </Stack>
  );
};

export default PaymentConfirmation;
